// src/qualify.ts
function qualifyCandidates(candidates, quantity) {
  return candidates.filter((c) => {
    if (c.minQuantity !== null && quantity < c.minQuantity) return false;
    if (c.maxQuantity !== null && quantity > c.maxQuantity) return false;
    return true;
  });
}

// src/strategies/lowest-wins.ts
var lowestWinsStrategy = {
  name: "lowest-wins",
  select({ candidates }) {
    const winner = candidates.reduce((a, b) => a.amount <= b.amount ? a : b);
    return {
      winner,
      qualifiedAlternatives: candidates.filter((c) => c !== winner)
    };
  }
};

// src/select-price.ts
function selectPrice(candidates, quantity, strategy = lowestWinsStrategy) {
  const qualified = qualifyCandidates(candidates, quantity);
  if (qualified.length === 0) return null;
  return strategy.select({ candidates: qualified, quantity });
}

// src/build-candidates.ts
function buildCandidatesForVariant(input) {
  const out = [];
  if (input.basePrice) {
    out.push({
      sourcePriceId: input.basePrice.id,
      amount: input.basePrice.amount,
      scope: "standard",
      ruleAttributes: [],
      minQuantity: null,
      maxQuantity: null
    });
  } else if (input.fallbackStandardPrice !== void 0) {
    out.push({
      sourcePriceId: null,
      amount: input.fallbackStandardPrice,
      scope: "standard",
      ruleAttributes: [],
      minQuantity: null,
      maxQuantity: null
    });
  }
  if (input.customerPriceLegacy) {
    out.push({
      sourcePriceId: input.customerPriceLegacy.id,
      amount: input.customerPriceLegacy.amount,
      scope: "customer",
      ruleAttributes: [],
      minQuantity: input.customerPriceLegacy.minQuantity,
      maxQuantity: input.customerPriceLegacy.maxQuantity
    });
  }
  for (const tier of input.bulkTiers) {
    out.push({
      sourcePriceId: tier.id,
      amount: tier.amount,
      scope: "global-tier",
      ruleAttributes: [],
      minQuantity: tier.minQuantity,
      maxQuantity: tier.maxQuantity
    });
  }
  for (const row of input.ruleBased) {
    const ruleAttrs = row.rules.map((r) => ({
      attribute: r.attribute,
      value: r.value
    }));
    const hasCustomerRule = row.rules.some((r) => r.attribute === "customer_uuid");
    const scope = hasCustomerRule ? "customer" : "org";
    out.push({
      sourcePriceId: row.id,
      amount: row.amount,
      scope,
      ruleAttributes: ruleAttrs,
      minQuantity: row.minQuantity,
      maxQuantity: row.maxQuantity
    });
  }
  return out;
}

// src/dto-candidates.ts
function parseAmount(raw) {
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : null;
}
function buildCandidatesFromProductPricingDto(input) {
  const candidates = [
    {
      sourcePriceId: null,
      amount: input.standardPrice,
      scope: "standard",
      ruleAttributes: [],
      minQuantity: null,
      maxQuantity: null
    }
  ];
  if (input.customerPrice !== void 0) {
    candidates.push({
      sourcePriceId: null,
      amount: input.customerPrice,
      scope: "customer",
      ruleAttributes: [],
      minQuantity: null,
      maxQuantity: null
    });
  }
  if (input.buyerPriceTiers?.length) {
    for (const tier of input.buyerPriceTiers) {
      const amount = parseAmount(tier.amount);
      if (amount === null) continue;
      candidates.push({
        sourcePriceId: tier.priceId,
        amount,
        scope: tier.scope,
        ruleAttributes: [],
        minQuantity: tier.fromQuantity,
        maxQuantity: tier.maxQuantity
      });
    }
  } else if (input.buyerPrice !== void 0) {
    candidates.push({
      sourcePriceId: null,
      amount: input.buyerPrice,
      scope: "org",
      ruleAttributes: [],
      minQuantity: null,
      maxQuantity: null
    });
  }
  for (const tier of input.discountTiers ?? []) {
    const amount = tier.hasCustomPrice ? parseAmount(tier.price) : Math.round(input.standardPrice * (1 - tier.percentage / 100) * 100) / 100;
    if (amount === null || !Number.isFinite(amount)) continue;
    candidates.push({
      sourcePriceId: null,
      amount,
      scope: "global-tier",
      ruleAttributes: [],
      minQuantity: tier.fromQuantity,
      maxQuantity: null
    });
  }
  return candidates;
}
export {
  buildCandidatesForVariant,
  buildCandidatesFromProductPricingDto,
  lowestWinsStrategy,
  qualifyCandidates,
  selectPrice
};
